// @givmo/passkeys — what a sign-in door shows when the company's sign-in lock refused the sign-in
// (AS-WEB27), in place of its red error line: never the server's raw words (today's frame B0), and
// never the red line at all — it is not the person's mistake. The card's other account-security
// notice look (the pending reset's), as the approved frames B1–B6 draw it:
//
//   "Your company manages how you sign in"
//   why, in one line;
//   the organization door, first and filled, where this page offers it (AS-WEB30) — the way that
//     keeps the job, so a current employee is never steered into unlinking;
//   one line a host may place here (Board's, on its director door from an invitation: D-CBG-23's
//     "join with another Givmo account", AS-WEB27's rider) — empty unless a host fills it;
//   then the way back in, for anyone who left or isn't set up: "Unlink your company".
//
// Signed out, it names no company (whoever holds the password reads it). A door shows it when its
// error hook says the refusal was the lock's (`AuthError` kind `organizationSsoRequired`: a blocking
// function's PERMISSION_DENIED, or the passkey mint's COMPANY_SSO_REQUIRED), and leaves its entry link
// out meanwhile: the notice carries the way back in itself.

import type { ReactNode } from 'react';
import { Button, Icon, cn, testIdProp } from '@givmo/design-system';
import { Building2 } from 'lucide-react';
import { LOCK_REFUSAL_WORDS as W } from './escape-hatch-copy';

const LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-warning-700';

export interface LockRefusalNoticeProps {
  /** Opens the way back in (the console's `/lost-company-access`). */
  onUnlink: () => void;
  /**
   * The organization door, offered first where this page offers it (its availability read said so),
   * worded as the page words its door: "Continue with …" or "Sign in with Organization SSO".
   */
  organizationSso?: { label: string; onOpen: () => void } | undefined;
  /**
   * One line a host places after the door and before the unlink paragraph — Board's, on its director
   * door reached from a board invitation. Nothing renders in the slot when it is absent.
   */
  line?: ReactNode;
  testId?: string;
}

export function LockRefusalNotice({
  onUnlink,
  organizationSso,
  line,
  testId = 'lock-refusal',
}: LockRefusalNoticeProps) {
  const hasLine = line !== undefined && line !== null && line !== false;
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-10 rounded-lg border border-warning-100 bg-warning-50 p-16"
      {...testIdProp(testId)}
    >
      <p className="font-sans text-s14 font-w600 tracking-givmo text-warning-700">{W.title}</p>
      <p className={LINE}>{W.body}</p>
      {organizationSso !== undefined && (
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          leadingIcon={<Icon icon={Building2} size={16} aria-hidden />}
          onClick={organizationSso.onOpen}
          testId={`${testId}-organization-sso`}
        >
          {organizationSso.label}
        </Button>
      )}
      {hasLine && (
        <div
          className={cn(LINE, organizationSso !== undefined && 'pt-4')}
          {...testIdProp(`${testId}-line`)}
        >
          {line}
        </div>
      )}
      <p className={cn(LINE, (organizationSso !== undefined || hasLine) && 'pt-4')}>{W.escape}</p>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={onUnlink}
        testId={`${testId}-unlink`}
      >
        {W.unlink}
      </Button>
    </div>
  );
}
